/**
 * @file JobTab.js
 * @description This component displays and manages job/employment information for an individual including employment status, compensation, and organizational structure. Supports both create and edit modes.
 */

import {
    useState,
    useEffect,
    useImperativeHandle,
    forwardRef,
    useCallback,
} from 'react';
import { getUsStatesTerritories } from '../../utils/utils';
import { Dropdown } from '../helpers/Dropdown';
import CustomDatePicker from '../helpers/CustomDatePicker_V2';
import Toast from '../ui/toasts/Toast';
import { updateIndividual } from '../../services/individualService';
import { isValidMoney } from '../../utils/validate';
import MoneyInput from '../shared/specialFormatInputs/MoneyInput';
import Tooltip from '../ui/tooltips/Tooltip';
import EmploymentCategoryDropdown from '../shared/asyncDropdowns/EmploymentCategoryDropdown';
import EmploymentStatusDropdown from '../shared/asyncDropdowns/EmploymentStatusDropdown';
import LocationDropdown from '../shared/asyncDropdowns/LocationDropdown';
import DepartmentDropdown from '../shared/asyncDropdowns/DepartmentDropdown';
import DivisionDropdown from '../shared/asyncDropdowns/DivisionDropdown';
import RegionDropdown from '../shared/asyncDropdowns/RegionDropdown';
import BusinessUnitDropdown from '../shared/asyncDropdowns/BusinessUnitDropdown';
import PayFrequencyDropdown from '../shared/asyncDropdowns/PayFrequencyDropdown';
import PayGroupDropdown from '../shared/asyncDropdowns/PayGroupDropdown';
import style from './IndividualPage.module.scss';
import { TenantUserStatusEnum } from '../../utils/data/enums';
import { getText } from '../../i18n';

const fullTimeOptions = [
    { value: '', label: getText('msg_7a7d6f0274ce') },
    { value: true, label: getText('msg_5397e0583f14') },
    { value: false, label: getText('msg_816c52fd2bdd') },
];

/**
 * Checks if the account is disabled and shows toast message if so
 * @param {Object} tenantUser - The tenant user object
 * @returns {boolean} True if account is disabled
 */
const checkAccountDisabled = tenantUser => {
    if (tenantUser?.tenantUserStatusId === TenantUserStatusEnum.Disabled) {
        Toast.error(
            getText('msg_66aa8e9b0915'),
            {},
            getText('msg_8d884d6c8bcb'),
        );
        return true;
    }
    return false;
};

/**
 * Checks if employment status is active
 * @param {number} statusId - The employment status ID
 * @returns {boolean} True if status is active
 */
const isActiveStatus = statusId => {
    return statusId === 1;
};

/**
 * Checks if form has minimum required fields filled for job tab
 * @param {object} data - The form data
 * @returns {boolean} True if form has required fields
 */
const checkFormValidity = data => {
    const hasCategory = data.employmentCategoryId;
    const hasStatus = data.employmentStatusId;

    return !!(hasCategory && hasStatus);
};

const JobTab = forwardRef(
    (
        {
            individualId,
            individualRecord,
            tenantUser,
            onSave,
            onDataChange,
            fieldNamingMap,
            defaultFieldNamingMap,
            isCreateMode = false,
        },
        ref,
    ) => {
        const [formData, setFormData] = useState({
            employmentCategoryId: null,
            employmentStatusId: null,
            workState: '',
            isFullTimeEmployee: null,
            salary: null,
            hourlyRate: null,
            payFrequencyId: null,
            payGroupId: null,
            hireDate: null,
            termDate: null,
            locationId: null,
            departmentId: null,
            divisionId: null,
            regionId: null,
            businessUnitId: null,
            jobTitle: '',
        });

        const [isLoading, setIsLoading] = useState(false);
        const [usStatesTerritories, setUsStatesTerritories] = useState([]);
        const [errors, setErrors] = useState({});

        useEffect(() => {
            const states = [
                { label: getText('msg_1bcf1fee269d'), value: '' },
                ...getUsStatesTerritories().map(e => ({
                    label: e.name,
                    value: e.acronym,
                })),
            ];
            setUsStatesTerritories(states);
        }, []);

        useEffect(() => {
            if (!individualRecord) return;

            if (isCreateMode) {
                setFormData(prev => ({
                    ...prev,
                    ...individualRecord,
                }));
                return;
            }

            setFormData({
                employmentCategoryId: individualRecord.employmentCategoryId,
                employmentStatusId: individualRecord.employmentStatusId,
                workState: individualRecord.workState || '',
                isFullTimeEmployee: individualRecord.isFullTimeEmployee,
                salary: individualRecord.salary,
                hourlyRate: individualRecord.hourlyRate,
                payFrequencyId: individualRecord.payFrequencyId,
                payGroupId: individualRecord.payGroupId,
                hireDate: individualRecord.hireDate,
                termDate: individualRecord.termDate,
                locationId: individualRecord.locationId,
                departmentId: individualRecord.departmentId,
                divisionId: individualRecord.divisionId,
                regionId: individualRecord.regionId,
                businessUnitId: individualRecord.businessUnitId,
                jobTitle: individualRecord.jobTitle || '',
            });
        }, [individualRecord, isCreateMode]);

        const notifyParentOfChange = useCallback(
            newFormData => {
                if (isCreateMode && onDataChange) {
                    const isValid = checkFormValidity(newFormData);
                    onDataChange(newFormData, isValid);
                }
            },
            [isCreateMode, onDataChange],
        );

        const handleInputChange = event => {
            const { name, value } = event.target;

            const updatedFormData = {
                ...formData,
                [name]: value === '' ? null : value,
            };

            setFormData(updatedFormData);
            notifyParentOfChange(updatedFormData);

            if (errors[name]) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    [name]: null,
                }));
            }
        };

        const handleDateChange = (fieldName, date) => {
            const updatedFormData = {
                ...formData,
                [fieldName]: date,
            };

            setFormData(updatedFormData);
            notifyParentOfChange(updatedFormData);
        };

        const handleDropdownChange = (fieldName, value) => {
            let updatedFormData = {
                ...formData,
                [fieldName]: value === '' ? null : value,
            };

            if (fieldName === 'employmentCategoryId') {
                updatedFormData = {
                    ...updatedFormData,
                    employmentStatusId: null,
                };
            }

            if (fieldName === 'employmentStatusId' && isActiveStatus(value)) {
                updatedFormData = {
                    ...updatedFormData,
                    termDate: null,
                };
            }

            setFormData(updatedFormData);
            notifyParentOfChange(updatedFormData);
        };

        const handleMoneyChange = (fieldName, value) => {
            const updatedFormData = {
                ...formData,
                [fieldName]: value,
            };

            setFormData(updatedFormData);
            notifyParentOfChange(updatedFormData);

            if (errors[fieldName]) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    [fieldName]: null,
                }));
            }
        };

        const validateForm = () => {
            const validationErrors = {};

            if (!formData.employmentCategoryId) {
                validationErrors.employmentCategoryId = 'Category is required.';
            }

            if (!formData.employmentStatusId) {
                validationErrors.employmentStatusId = 'Status is required.';
            }

            if (formData.salary) {
                const salaryError = isValidMoney(formData.salary, 'Salary');
                if (salaryError) {
                    validationErrors.salary = salaryError;
                }
            }

            if (formData.hourlyRate) {
                const hourlyRateError = isValidMoney(
                    formData.hourlyRate,
                    'Hourly Rate',
                );
                if (hourlyRateError) {
                    validationErrors.hourlyRate = hourlyRateError;
                }
            }

            setErrors(validationErrors);

            if (Object.keys(validationErrors).length > 0) {
                const firstError = Object.values(validationErrors)[0];
                Toast.error(firstError);
                return false;
            }

            return true;
        };

        const handleSave = async event => {
            event?.preventDefault();

            if (!isCreateMode && checkAccountDisabled(tenantUser)) return;

            if (!validateForm()) return;

            if (isCreateMode) {
                return true;
            }

            try {
                setIsLoading(true);

                const payload = {
                    ...individualRecord,
                    ...formData,
                };

                await updateIndividual(payload);
                Toast.success(getText('msg_a4b8f441e438'));
                onSave();
            } catch (error) {
                console.error('Unable to save changes', error);
                if (error?.response?.data?.detail) {
                    Toast.error(error.response.data.detail);
                } else {
                    Toast.error(getText('msg_3c2ce6f3c1a9'));
                }
            } finally {
                setIsLoading(false);
            }
        };

        useImperativeHandle(ref, () => ({
            handleSave,
            validateForm,
            getFormData: () => ({ ...formData }),
        }));

        return (
            <form className={style.jobTabContainer}>
                <div className="row px-5 py-4">
                    {/* Row 1: Category, Hire Date */}
                    <div className="col-12 col-md-6 mb-3">
                        <EmploymentCategoryDropdown
                            selectedCategory={formData.employmentCategoryId}
                            onCategoryChange={value =>
                                handleDropdownChange(
                                    'employmentCategoryId',
                                    value,
                                )
                            }
                            isRequired={true}
                            label={getText('msg_a3c686e711e4')}
                            errorMessage={errors.employmentCategoryId}
                            hideErrorMessage={true}
                            inlineLabel={true}
                        />
                    </div>

                    <div className="col-12 col-md-6 mb-3">
                        <div className="form-group mb-3 inline-input">
                            <label htmlFor="hireDate">
                                {getText('msg_a8d919de0525')}
                            </label>
                            <CustomDatePicker
                                selectedDate={formData.hireDate}
                                onDateChange={date =>
                                    handleDateChange('hireDate', date)
                                }
                            />
                        </div>
                    </div>

                    {/* Row 2: Status, Term Date */}
                    <div className="col-12 col-md-6 mb-3">
                        <EmploymentStatusDropdown
                            selectedStatus={formData.employmentStatusId}
                            onStatusChange={value =>
                                handleDropdownChange(
                                    'employmentStatusId',
                                    value,
                                )
                            }
                            isRequired={true}
                            label={getText('msg_bae7d5be7082')}
                            errorMessage={errors.employmentStatusId}
                            hideErrorMessage={true}
                            disabled={!formData.employmentCategoryId}
                            showToolTip={!formData.employmentCategoryId}
                            tooltipText={getText('msg_4956e9105670')}
                        />
                    </div>

                    <div className="col-12 col-md-6 mb-3">
                        <div className="form-group mb-3 inline-input">
                            <label htmlFor="termDate">
                                {getText('msg_d6f6ed1008e7')}
                                {isActiveStatus(
                                    formData.employmentStatusId,
                                ) && (
                                        <Tooltip
                                            html="Term Date cannot be set when Status is Active"
                                            placement="right"
                                        >
                                            <i
                                                className="bi bi-info-circle"
                                                style={{
                                                    paddingLeft: '5px',
                                                    fontSize: '13px',
                                                    color: 'var(--iNGAGED-Primary-Color)',
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                            </label>
                            <CustomDatePicker
                                selectedDate={formData.termDate}
                                onDateChange={date =>
                                    handleDateChange('termDate', date)
                                }
                                disabled={isActiveStatus(
                                    formData.employmentStatusId,
                                )}
                                disableFutureDates={false}
                            />
                        </div>
                    </div>

                    {/* Row 3: Work State, Location */}
                    <div className="col-12 col-md-6 mb-3">
                        <div className="form-group mb-3 inline-input">
                            <label htmlFor="workState">
                                {getText('msg_9210e20522ea')}
                            </label>
                            <Dropdown
                                name="workState"
                                defaultValue={
                                    usStatesTerritories.find(
                                        state =>
                                            state.value === formData.workState,
                                    )?.label || 'Select State'
                                }
                                options={usStatesTerritories}
                                onChange={value =>
                                    handleDropdownChange('workState', value)
                                }
                                value={formData.workState || ''}
                                disabled={usStatesTerritories.length === 0}
                            />
                        </div>
                    </div>

                    <div className="col-12 col-md-6 mb-3">
                        <LocationDropdown
                            selectedLocation={formData.locationId}
                            onLocationChange={value =>
                                handleDropdownChange('locationId', value)
                            }
                        />
                    </div>

                    {/* Row 4: Full Time, Department */}
                    <div className="col-12 col-md-6 mb-3">
                        <div className="form-group mb-3 inline-input">
                            <label htmlFor="isFullTimeEmployee">
                                {getText('msg_c8a83b01e302')}
                            </label>
                            <Dropdown
                                name="isFullTimeEmployee"
                                defaultValue={
                                    fullTimeOptions.find(
                                        opt =>
                                            opt.value ===
                                            formData.isFullTimeEmployee,
                                    )?.label || 'Select Option'
                                }
                                options={fullTimeOptions}
                                onChange={value =>
                                    handleDropdownChange(
                                        'isFullTimeEmployee',
                                        value === '' ? null : value,
                                    )
                                }
                                value={formData.isFullTimeEmployee ?? ''}
                            />
                        </div>
                    </div>

                    <div className="col-12 col-md-6 mb-3">
                        <DepartmentDropdown
                            selectedDepartment={formData.departmentId}
                            onDepartmentChange={value =>
                                handleDropdownChange('departmentId', value)
                            }
                        />
                    </div>

                    {/* Row 5: Salary, Division */}
                    <div className="col-12 col-md-6 mb-3">
                        <MoneyInput
                            name="salary"
                            label={getText('msg_595a803cf39d')}
                            value={formData.salary}
                            onChange={value =>
                                handleMoneyChange('salary', value)
                            }
                            disabled={!!formData.hourlyRate}
                            errorMessage={errors.salary}
                            hideErrorMessage={true}
                            showToolTip={!!formData.hourlyRate}
                            tooltipText={getText('msg_64e0a6f4e779')}
                            inlineLabel={true}
                        />
                    </div>

                    <div className="col-12 col-md-6 mb-3">
                        <DivisionDropdown
                            selectedDivision={formData.divisionId}
                            onDivisionChange={value =>
                                handleDropdownChange('divisionId', value)
                            }
                        />
                    </div>

                    {/* Row 6: Hourly Rate, Region */}
                    <div className="col-12 col-md-6 mb-3">
                        <MoneyInput
                            name="hourlyRate"
                            label={getText('msg_26ede189a778')}
                            value={formData.hourlyRate}
                            onChange={value =>
                                handleMoneyChange('hourlyRate', value)
                            }
                            disabled={!!formData.salary}
                            errorMessage={errors.hourlyRate}
                            hideErrorMessage={true}
                            showToolTip={!!formData.salary}
                            tooltipText={getText('msg_b1813a75adc7')}
                            inlineLabel={true}
                        />
                    </div>

                    <div className="col-12 col-md-6 mb-3">
                        <RegionDropdown
                            selectedRegion={formData.regionId}
                            onRegionChange={value =>
                                handleDropdownChange('regionId', value)
                            }
                        />
                    </div>

                    {/* Row 7: Pay Frequency, Business Unit */}
                    <div className="col-12 col-md-6 mb-3">
                        <PayFrequencyDropdown
                            selectedFrequency={formData.payFrequencyId}
                            onFrequencyChange={value =>
                                handleDropdownChange('payFrequencyId', value)
                            }
                            isRequired={false}
                            label={getText('msg_9f5a370f9db2')}
                        />
                    </div>

                    <div className="col-12 col-md-6 mb-3">
                        <BusinessUnitDropdown
                            selectedBusinessUnit={formData.businessUnitId}
                            onBusinessUnitChange={value =>
                                handleDropdownChange('businessUnitId', value)
                            }
                        />
                    </div>

                    {/* Row 8: Pay Group, Job Title */}
                    <div className="col-12 col-md-6 mb-3">
                        <PayGroupDropdown
                            selectedPayGroup={formData.payGroupId}
                            onPayGroupChange={value =>
                                handleDropdownChange('payGroupId', value)
                            }
                        />
                    </div>

                    <div className="col-12 col-md-6 mb-3">
                        <div className="form-group mb-3 inline-input">
                            <label htmlFor="jobTitle">
                                {getText('msg_d42977925117')}
                            </label>
                            <input
                                id="jobTitle"
                                type="text"
                                maxLength="128"
                                className="form-control"
                                value={formData.jobTitle || ''}
                                onChange={handleInputChange}
                                name="jobTitle"
                            />
                        </div>
                    </div>
                </div>
            </form>
        );
    },
);

export default JobTab;
